import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import Input from '../Input';
import { Tree, Checkbox } from '@firesoon/antd';
import { includesStr } from './config';
import { useDebounce, useSetState } from 'ahooks';

const { TreeNode } = Tree;

const Index = ({
  treeData,
  treeNodeHeight,
  checkedList,
  defaultExpandedKeys,
  showSearch,
  showCheckAll,
  visible,
  searchPlaceholder,
  onCheck,
  onCheckAll,
  searchRef,
  logValue,
}: any) => {
  const [{ expandedKeys, search, hasAllChecked, indeterminate }, setState] = useSetState<any>({
    expandedKeys: [],
    search: '',
    hasAllChecked: false,
    indeterminate: false,
  });
  const debouncedSearch = useDebounce(search, { wait: 300 });
  const { checkAllKeys, checkedKeys } = useMemo(() => {
    let arr: any = [];
    treeData.forEach((item: any) => {
      if (item?.children?.length > 0) {
        item.children.forEach((obj: any) => arr.push(obj.key));
      } else {
        arr.push(item.key);
      }
    });

    return {
      checkAllKeys: arr, // 过滤掉一级节点含子元素的，其他树节点key集合
      checkedKeys: checkedList.map((item) => item.key)?.filter((item) => arr.includes(item)), // 过滤掉一级节点含子元素的节点key
    };
  }, [treeData, checkedList]);

  useEffect(() => {
    const checkedAll = checkedKeys.length === checkAllKeys.length && checkedKeys.length > 0;
    const indeterminate = !checkedAll && checkedKeys.length > 0;

    setState({
      hasAllChecked: checkedAll,
      indeterminate: indeterminate,
    });
  }, [checkAllKeys, checkedKeys]);

  useLayoutEffect(() => {
    if (visible) {
      searchRef?.current?.input.focus();

      setState({
        search: '',
      });
    }
  }, [visible]);

  useEffect(() => {
    if (defaultExpandedKeys?.length) {
      setState({
        expandedKeys: defaultExpandedKeys,
      });
    }
  }, [defaultExpandedKeys]);

  const getTitle = (title, searchValue) => {
    if (searchValue) {
      const index = title.toUpperCase().indexOf(searchValue.toUpperCase());
      const beforeStr = title.substr(0, index);
      const afterStr = title.substr(index + searchValue.length);
      const curStr = title.substr(index, searchValue.length);

      const titleDom =
        index > -1 ? (
          <span title={title}>
            {beforeStr}
            <span className="fs-tree-search-highlight">{curStr}</span>
            {afterStr}
          </span>
        ) : (
          <span title={title}>{title}</span>
        );

      return titleDom;
    }

    return title;
  };

  const renderNode = useCallback(
    (data) => {
      const node = data.map((item) => {
        if (item.children?.length > 0) {
          if (
            includesStr(item.title, debouncedSearch) ||
            item?.children?.some((child) => includesStr(child.title, debouncedSearch))
          ) {
            return (
              <TreeNode
                data-log-value={logValue}
                title={getTitle(item.title, debouncedSearch)}
                key={item.key}
                dataRef={item}
              >
                {item.children.map((c) => (
                  <TreeNode data-log-value={logValue} key={c.key} title={getTitle(c.title, debouncedSearch)} />
                ))}
              </TreeNode>
            );
          }
        }
        return includesStr(item.title, debouncedSearch) ? (
          <TreeNode data-log-value={logValue} title={getTitle(item.title, debouncedSearch)} key={item.key} />
        ) : null;
      });
      if (node.find((i) => i)) {
        return node;
      }
      return null;
    },
    [debouncedSearch],
  );

  const onExpand = (keys) => {
    setState({
      expandedKeys: keys,
    });
  };

  const treeNode = renderNode(treeData);

  useEffect(() => {
    if (debouncedSearch) {
      setState({
        expandedKeys: treeNode?.map((item) => item?.key).filter((item) => item != null),
      });
    }
  }, [debouncedSearch]);

  const onChange = (e) => {
    const value = e.target.value.trim();

    setState({
      search: value,
    });
  };

  const handleAllChecked = (e) => {
    setState({ hasAllChecked: e.target.checked });
    onCheckAll(e.target.checked ? checkAllKeys : []);
  };

  /** 动态类名 */
  const extraClassName = useMemo(() => {
    const names = ['defalut', showSearch ? 'input' : '', showCheckAll ? 'check' : ''].filter((item) => item);
    return `fs-multiple-select-tree-opts-search-${names.join('-')}`;
  }, [showSearch, showCheckAll]);

  return (
    <div>
      <div className={`fs-multiple-select-tree-opts-search ${extraClassName}`}>
        {showSearch && <Input.Search placeholder={searchPlaceholder} onChange={onChange} ref={searchRef} allowClear />}
        {showCheckAll && (
          <Checkbox onChange={handleAllChecked} checked={hasAllChecked} indeterminate={indeterminate}>
            <span className="check-all">全选</span>
          </Checkbox>
        )}
      </div>

      {treeNode ? (
        <div className="fs-multiple-select-tree-opts-list" style={{ maxHeight: 300, height: treeNodeHeight }}>
          <Tree
            checkable={true}
            selectable={false}
            checkedKeys={checkedKeys}
            onCheck={onCheck}
            expandedKeys={expandedKeys}
            onExpand={onExpand}
          >
            {treeNode}
          </Tree>
        </div>
      ) : (
        <p className="fs-multiple-select-tree-search-empty">无搜索结果</p>
      )}
    </div>
  );
};

export default Index;
